import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getNit } from "@/api/nits";
import { Button } from "@/components/ui/Button";
import { PageHeader } from "@/components/ui/PageHeader";
import { FormPageSkeleton } from "@/components/ui/FormPageSkeleton";
import { KeyValueRow } from "@/components/ui/KeyValueRow";
import { InvalidIdNotice } from "@/components/ui/InvalidIdNotice";
import { useAuth } from "@/contexts/AuthContext";

export const NitVerPage = () => {
  const { isAdmin, hasPermission } = useAuth();
  const canEdit = isAdmin || hasPermission("editar_nits");
  const { nit: nitParam } = useParams<{ nit: string }>();
  const nitOriginal = decodeURIComponent(nitParam ?? "");

  const { data: nit, isLoading } = useQuery({
    queryKey: ["nit", nitOriginal],
    queryFn: () => getNit(nitOriginal),
    enabled: Boolean(nitOriginal),
  });

  useEffect(() => {
    if (nit) document.title = `${nit.razon_social} — NIT`;
  }, [nit]);

  if (!nitOriginal) {
    return <InvalidIdNotice label="NIT" />;
  }
  if (isLoading || !nit) {
    return <FormPageSkeleton variant="detail" />;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader
        title="Detalle del NIT"
        actions={
          canEdit ? (
            <Link to={`/nits/${encodeURIComponent(nitOriginal)}/editar`}>
              <Button>Editar</Button>
            </Link>
          ) : null
        }
      />

      <div className="rounded-lg bg-white p-6 shadow-card">
        <KeyValueRow label="NIT" value={nit.nit} />
        <KeyValueRow label="DV" value={nit.dv} />
        <KeyValueRow
          label="Razón Social"
          value={nit.razon_social?.toUpperCase()}
        />
        <KeyValueRow label="Teléfono" value={nit.telefono} />
        <KeyValueRow label="Correo" value={nit.correo_electronico} />
        <KeyValueRow label="Dirección" value={nit.direccion?.toUpperCase()} />
        <KeyValueRow label="Ciudad" value={nit.ciudad?.toUpperCase()} />
        <KeyValueRow label="País" value={nit.pais?.toUpperCase()} />
        {nit.created_at ? (
          <KeyValueRow
            label="Creado"
            value={new Date(nit.created_at).toLocaleString("es-CO")}
          />
        ) : null}
        {nit.updated_at ? (
          <KeyValueRow
            label="Actualizado"
            value={new Date(nit.updated_at).toLocaleString("es-CO")}
          />
        ) : null}
      </div>
    </div>
  );
};
